import {
  CpActivity,
  CpActivityStatus,
  CpBarisMatriks,
  CpGrupSeksi,
  CpHari,
  CpPenanda,
  CpPpaRole,
  CpSeksi,
  CpVarianceKategori
} from "@/types/cp";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function keDate(v: unknown): Date | null {
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : v;
  }
  if (typeof v !== "string" || !v.trim()) {
    return null;
  }

  const teks = v.trim().replace("T", " ");
  const [tanggal, jam] = teks.split(" ");
  const [y, m, d] = tanggal.split("-").map(Number);
  if (!y || !m || !d) {
    return null;
  }

  let jj = 0;
  let mm = 0;
  if (jam) {
    const bagian = jam.split(":");
    jj = Number(bagian[0]) || 0;
    mm = Number(bagian[1]) || 0;
  }

  return new Date(y, m - 1, d, jj, mm, 0, 0);
}

export function keTeksDateTime(v: unknown): string | null {
  const d = keDate(v);
  if (!d) return null;

  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    ` ${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export const HARI_LABELS: Record<CpHari, string> = {
  h1: "Hari Rawat 1",
  h2: "Hari Rawat 2",
  h3: "Hari Rawat 3",
  h4: "Hari Rawat 4"
};

export const SEKSI_ORDER: CpSeksi[] = [
  "asesmen_awal",
  "laboratorium",
  "radiologi",
  "konsultasi",
  "asesmen_lanjutan",
  "diagnosis",
  "discharge_planning",
  "edukasi_terintegrasi",
  "terapi_medikamentosa",
  "tli",
  "monitoring_evaluasi",
  "mobilisasi",
  "hasil",
  "kriteria_pulang",
  "rencana_pulang"
];

export const PENANDA_LABELS: Record<CpPenanda, string> = {
  rutin: "Yang harus dilakukan (○)",
  indikasi: "Bisa atau tidak (Δ)",
  tanpa: "Baris isian",
  varian: "Baris varian"
};

export const PPA_LABELS: Record<CpPpaRole, string> = {
  dpjp_obg: "DPJP Sp.OG",
  dokter_jaga: "Dr. Jaga",
  anestesi: "Dr. Anestesi",
  bidan: "Bidan",
  perawat: "Perawat",
  apoteker: "Apoteker",
  dietisien: "Dietisien",
  analis: "Analis Lab",
  mpp: "MPP",
  psikolog: "Psikolog",
  konselor_kb: "Konselor KB"
};

export const STATUS_LABELS: Record<CpActivityStatus, string> = {
  not_started: "Belum",
  done: "Sudah dilakukan",
  na: "Tidak dilakukan (N/A)",
  variance: "Varian"
};

export const KATEGORI_VARIANCE_LABELS: Record<CpVarianceKategori, string> = {
  patient: "Pasien",
  provider: "Klinis / PPA",
  system: "Sistem",
  family: "Keluarga"
};

export const DAMPAK_LABELS: Record<string, string> = {
  none: "Tidak ada",
  minor: "Minor",
  major: "Major"
};

export function glyphOf(activity: CpActivity | null | undefined): string {
  if (!activity || activity.penanda === "tanpa" || activity.penanda === "varian") {
    return "";
  }
  if (activity.status === "na") {
    return "–";
  }
  if (activity.status === "variance") {
    return "✗";
  }
  const dasar = activity.penanda === "indikasi" ? "Δ" : "○";
  return activity.status === "done" ? dasar + "√" : dasar;
}

export function glyphUntukStatus(
  activity: CpActivity,
  status: CpActivityStatus
): string {
  return glyphOf({ ...activity, status } as CpActivity);
}

export function labelBaris(baris: CpBarisMatriks, hari: CpHari): string {
  const bagian = [baris.seksi_label];
  if (baris.sub_seksi_label) {
    bagian.push(baris.sub_seksi_label);
  }
  bagian.push(baris.aktivitas_label);
  bagian.push(HARI_LABELS[hari]);
  return bagian.join(" · ");
}

export function rowspanSubSeksi(
  baris: CpBarisMatriks[]
): Record<number, number> {
  const span: Record<number, number> = {};
  let awal = -1;
  let sekarang: string | null | undefined;

  baris.forEach((b, i) => {
    if (awal < 0 || b.sub_seksi_label !== sekarang) {
      awal = i;
      sekarang = b.sub_seksi_label;
      span[awal] = 1;
    } else {
      span[awal] += 1;
    }
  });

  return span;
}

export function grupkanMatriks(activities: CpActivity[]): CpGrupSeksi[] {
  const perBaris: Record<string, CpBarisMatriks> = {};
  const urutanBaris: string[] = [];

  activities.forEach(a => {
    if (!perBaris[a.baris_kode]) {
      perBaris[a.baris_kode] = {
        baris_kode: a.baris_kode,
        seksi: a.seksi,
        seksi_label: a.seksi_label,
        sub_seksi_label: a.sub_seksi_label,
        aktivitas_label: a.aktivitas_label,
        keterangan: a.keterangan,
        penanda: a.penanda,
        sel: {}
      };
      urutanBaris.push(a.baris_kode);
    }
    perBaris[a.baris_kode].sel[a.hari] = a;
  });

  const grup: CpGrupSeksi[] = [];
  const indeks: Partial<Record<CpSeksi, number>> = {};

  urutanBaris.forEach(kode => {
    const baris = perBaris[kode];
    if (indeks[baris.seksi] === undefined) {
      indeks[baris.seksi] = grup.length;
      grup.push({
        seksi: baris.seksi,
        seksi_label: baris.seksi_label,
        ada_sub: false,
        baris: [],
        span: {}
      });
    }
    const g = grup[indeks[baris.seksi] as number];
    g.baris.push(baris);
    if (baris.sub_seksi_label) {
      g.ada_sub = true;
    }
  });

  grup.forEach(g => {
    g.span = rowspanSubSeksi(g.baris);
  });

  return grup;
}

export function selPertama(baris: CpBarisMatriks): CpActivity | null {
  const kunci = Object.keys(baris.sel) as CpHari[];
  return kunci.length ? (baris.sel[kunci[0]] as CpActivity) : null;
}
